import { ApprovalQueueDetailItemType, RequestDetailItemType } from '../common-types'
import { endpoints } from '../config'
import { getApiClient, postApiClient, putApiClient } from './api-client'
import mockRequests from './mockdata/mockdata-requests.json'

export async function getMockRequests() {
  return mockRequests
}

export async function getAllRequests({
  employeeId = '',
  date = '',
  eventName = '',
  venueName = '',
  requestNumber = '',
  page = 1,
  pageSize = 15,
} = {}) {
  const params = {
    pageObject: JSON.stringify({
      skip: '0',
      take: pageSize,
      page,
      pageSize,
    }),
    date,
    eventName,
    venueName,
    requestNumber,
    employeeId,
    eventId: '',
    // eventStatus: '',
    page,
    pageSize,
  }

  return getApiClient({ endpoint: endpoints.getRequests, params })
}

export function getRequestDetail(
  { requestNumber } = { requestNumber: '' }
): Promise<RequestDetailItemType> {
  const params = {
    requestNumber: requestNumber,
  }
  return getApiClient({ endpoint: endpoints.getRequestDetail, params })
}

export interface RequestAuditHistoryParams {
  requestId: string
  requestNumber: string | number
}

export interface RequestAuditHistoryBaseLog {
  modifiedby?: string | null
  modifieddate?: string | null
}

export interface RequestAuditHistoryGroupedLog<T> extends RequestAuditHistoryBaseLog {
  requestid?: string | null
  details?: T[]
}

export interface RequestAuditHistoryRequestInfoLog extends RequestAuditHistoryBaseLog {
  id?: string
  requestnumber?: string | number
  submittedby?: string | null
  status?: string | null
  eventname?: string | null
  eventdatetime?: string | null
  totaltickets?: number | string | null
  purposetype?: string | null
  requestcreateddate?: string | null
  firstname?: string | null
  lastname?: string | null
  requestedfor?: string | null
  mobilenumber?: string | null
  recipientemail?: string | null
  isworkingatvenue?: string | number | null
  purpose?: string | null
  clientname?: string | null
  clienttitle?: string | null
  clientcompany?: string | null
  sentremarks?: string | null
  lastconfirmationsent?: string | null
  updateorelse?: string | number | null
}

export interface RequestAuditHistoryGuestInfoLog extends RequestAuditHistoryBaseLog {
  requestid?: string
  guestnumber?: number | string | null
  guestname?: string | null
  guestemail?: string | null
  guestphone?: string | null
  guestrelation?: string | null
}

export interface RequestAuditHistoryPaymentInfoLog extends RequestAuditHistoryBaseLog {
  requestid?: string
  purposetype?: string | null
  paymenttype?: string | null
  paymentstatus?: string | null
  paymentmodifieddate?: string | null
  paymentmodifiedby?: string | null
  tickets?: number | string | null
  emailaddress?: string | null
  requestedforemail?: string | null
  lastsentdate?: string | null
}

export interface RequestAuditHistoryPricePreferenceInfoLog extends RequestAuditHistoryBaseLog {
  requestid?: string
  seatinglocation?: string | null
  price?: string | number | null
  preferenceorder?: number | string | null
}

export interface RequestAuditHistoryApprovalStep extends RequestAuditHistoryBaseLog {
  id?: string
  status?: string | null
  code?: string | null
  step?: number | string | null
  comments?: string | null
  usersInfo?: {
    changedby?: string | null
  } | null
  changedByEmployee?: {
    changedByEmployee?: string | null
  } | null
}

export interface RequestAuditHistoryApprovalLog {
  name?: string
  request?: {
    id?: string
    requestnumber?: string | number
    status?: string | null
  }
  workflowStateInstances?: RequestAuditHistoryApprovalStep[]
}

export interface RequestAuditHistoryResponse {
  success: boolean
  RequestInfoLog?: Array<
    RequestAuditHistoryRequestInfoLog | RequestAuditHistoryGroupedLog<RequestAuditHistoryRequestInfoLog>
  >
  GuestInfoLog?: Array<
    RequestAuditHistoryGuestInfoLog | RequestAuditHistoryGroupedLog<RequestAuditHistoryGuestInfoLog>
  >
  PaymentInfoLog?: Array<
    RequestAuditHistoryPaymentInfoLog | RequestAuditHistoryGroupedLog<RequestAuditHistoryPaymentInfoLog>
  >
  PricePreferenceInfoLog?: Array<
    | RequestAuditHistoryPricePreferenceInfoLog
    | RequestAuditHistoryGroupedLog<RequestAuditHistoryPricePreferenceInfoLog>
  >
  SeatingInfoLog?: RequestAuditHistoryBaseLog[]
  ApprovalHistory?: RequestAuditHistoryApprovalLog[]
}

export function getRequestAuditHistory(
  params: RequestAuditHistoryParams
): Promise<RequestAuditHistoryResponse> {
  return getApiClient({ endpoint: endpoints.requestAuditHistory, params })
}

export function cancelTicketRequest({
  requestId,
  employeeId,
}: {
  requestId: string
  employeeId: string
}) {
  const params = {
    requestId: String(requestId),
    employeeId,
  }
  return postApiClient({ endpoint: endpoints.cancelTicketRequest, params })
}

export interface GetRequestLockedStatus {
  employeeId: string
  lockedByName: string
  requestNumber: string | number
  eventId: string
}
export async function getRequestLockedStatus(
  params: GetRequestLockedStatus
): Promise<ApprovalQueueDetailItemType> {
  return getApiClient({ endpoint: endpoints.getRequestLockedStatus, params })
}

export interface ReleaseRowLocksParams {
  eventId: string
  employeeId: string
  userName?: string
  /** When provided, releases only this request's lock instead of every lock in the event. */
  requestNumber?: string | number
}

export async function releaseRowLocksForEvent(params: ReleaseRowLocksParams) {
  return putApiClient({
    endpoint: endpoints.releaseRowLocks,
    params: {
      eventId: params.eventId,
      employeeId: params.employeeId,
      ...(params.userName !== undefined ? { userName: params.userName } : {}),
      ...(params.requestNumber !== undefined &&
      params.requestNumber !== null &&
      `${params.requestNumber}` !== ''
        ? { requestNumber: params.requestNumber }
        : {}),
    },
  })
}

export function sendEmailConfirmationRequest(
  requestNumber: string | number,
  ticketLocation: string,
  confirmationSentBy: string
) {
  const params = {
    requestNumber,
    ticketLocation,
    confirmationSentBy,
  }
  return postApiClient({ endpoint: endpoints.sendTicketConfirmationEmail, params })
}

export interface ReorderRequestsPayload {
  eventId: string
  movedRequestId: string
  fromIndex: string //number
  toIndex: number
  userId: string
  userName: string
}

/** Max wait for reorder POST before treating as failed (ms). */
export const REORDER_REQUEST_TIMEOUT_MS = 30_000

export async function reorderRequests(
  payload: ReorderRequestsPayload
): Promise<ApprovalQueueDetailItemType[]> {
  return postApiClient({
    endpoint: endpoints.reorderRequests,
    payload,
    config: { timeout: REORDER_REQUEST_TIMEOUT_MS },
  })
}

interface CanUserApproveRequestType {
  employeeid: string
  id: string
  state: string
}
interface CanUserApproveResponseType {
  canUserApprove: boolean
}
export async function canUserApproveRequest(
  params: CanUserApproveRequestType
): Promise<CanUserApproveResponseType> {
  return getApiClient({ endpoint: endpoints.canUserApproveRequest, params })
}
